/* eslint-disable @typescript-eslint/no-explicit-any */
import * as FileSaver from 'file-saver';
import { message } from 'antd';
import * as XLSX from 'xlsx';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import appAtom from '../atoms/auth/app.atom';
import authAtom from '../atoms/auth/auth.atom';
import moment from 'moment';

const SENSITIVE_KEYS = ['uuid', 'id', 'password', 'secret', 'key'];
const CURRENCY_KEYS = ['amount', 'balance', 'total', 'sum', 'price', 'cost'];

const isCurrencyKey = (key: string) => {
  return CURRENCY_KEYS.some((ck) => key.toLowerCase().includes(ck));
};

export const formatNumber = (num: number | string | null) => {
  if (num !== undefined) {
    return parseFloat(Number(num).toFixed(2))
      .toString()
      .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
  }
  return '0';
};

export const formatCurrency = (value: number, currency?: string) =>
  new Intl.NumberFormat('en-'.concat(currency?.slice(0, 2) || 'NG'), {
    style: 'currency',
    currency: currency || 'NGN',
  })
    .format(value)
    .replace('.00', '');

export const formatAmount = (value: number) =>
  value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatHeaderLabel = (key: string) => {
  return key
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toUpperCase();
};

export const flattenObject = (obj: any, prefix = '', res: any = {}) => {
  for (const key in obj) {
    const propName = prefix ? `${prefix}_${key}` : key;
    if (
      typeof obj[key] === 'object' &&
      obj[key] !== null &&
      !Array.isArray(obj[key])
    ) {
      flattenObject(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
};

export const handleDataExport = (
  name: string,
  parseData: any,
  keyMap?: Record<string, string>,
  summaryRow?: Record<string, any>
) => {
  const dataToExport = Array.isArray(parseData)
    ? parseData.map((item) => {
      const flattenedItem = flattenObject(item);
      const newItem: any = {};

      if (keyMap) {
        // If keyMap is provided, only include those keys and use their mapped names
        for (const [dataKey, label] of Object.entries(keyMap)) {
          if (Object.prototype.hasOwnProperty.call(flattenedItem, dataKey)) {
            let value = flattenedItem[dataKey];
            if (isCurrencyKey(dataKey) && typeof value === 'number') {
              value = formatCurrency(value);
            }
            newItem[label] = value;
          }
        }
      } else {
        // Fallback to original behavior, but filter out sensitive keys
        for (const key in flattenedItem) {
          const isSensitive = SENSITIVE_KEYS.some((s) =>
            key.toLowerCase().includes(s)
          );
          if (
            Object.prototype.hasOwnProperty.call(flattenedItem, key) &&
            !isSensitive
          ) {
            let value = flattenedItem[key];
            if (isCurrencyKey(key) && typeof value === 'number') {
              value = formatCurrency(value);
            }
            newItem[formatHeaderLabel(key)] = value;
          }
        }
      }
      return newItem;
    })
    : parseData;

  if (summaryRow && Array.isArray(dataToExport)) {
    const firstKey = keyMap ? Object.values(keyMap)[0] : Object.keys(dataToExport[0] || {})[0];
    const amountKey = keyMap ? Object.values(keyMap).find(v => v === 'Amount') ?? 'Amount' : 'Amount';

    // Blank separator row
    dataToExport.push({});

    // Per-section summaries
    if (Array.isArray(summaryRow.sections)) {
      summaryRow.sections.forEach((section: { label: string; rows: { label: string; value: any }[] }) => {
        // Section header
        dataToExport.push({ [firstKey]: `── ${section.label} ──` });

        // Section summary rows
        section.rows.forEach((row) => {
          dataToExport.push({
            [firstKey]: row.label,
            [amountKey]: row.value,
          });
        });

        dataToExport.push({}); // blank between sections
      });
    }

    // Roundup block
    if (Array.isArray(summaryRow.roundup) && summaryRow.roundup.length > 0) {
      dataToExport.push({ [firstKey]: '── Roundup ──' });
      summaryRow.roundup.forEach((row: { label: string; value: any }) => {
        dataToExport.push({
          [firstKey]: row.label,
          [amountKey]: row.value,
        });
      });
    }
  }
  const ws = XLSX.utils.json_to_sheet(dataToExport);

  // Auto-size columns and add filters
  if (Array.isArray(dataToExport) && dataToExport.length > 0) {
    const keys = Object.keys(dataToExport[0]);
    ws['!cols'] = keys.map((key) => ({
      wch:
        Math.max(
          key.length,
          ...dataToExport.map((row) => (row[key]?.toString() || '').length)
        ) + 2,
    }));

    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    ws['!autofilter'] = { ref: XLSX.utils.encode_range(range) };
  }

  const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });
  FileSaver.saveAs(data, `Estateplus-${name}-${moment(new Date).format("LL")}.xlsx`);
};

export const handleDataReduce = (data: any, key: string) => {
  return data?.reduce((a: any, b: any) => Number(a) + Number(b?.[key] || b), 0);
};

export const handleObj = (data: any) =>
  Object.fromEntries(Object.entries(data || {})?.filter(([, val]) => val));

export const handleCopy = (content: string) =>
  navigator.clipboard
    .writeText(content)
    .then((res) =>
      message.success('Copied to clipboard'.concat(String(res) || '!'))
    )
    .catch((err) => message.error(err || 'Copy failed'));

export const isEqual = (data: any, key: any) => data === key;

export const handleEmailHash = (data: string) => {
  const mailName = (data || '')?.slice(0, (data || '')?.indexOf('@'));
  const mailProvider = (data || '')?.slice((data || '')?.indexOf('@'));
  const mailNameCut = mailName.slice(0, Math.floor(mailName.length / 2));
  return mailNameCut.padEnd(mailName.length, '*').concat(mailProvider) || '';
};

export const handleCapitalize = (data: string) =>
  (data || '').charAt(0).toUpperCase() + (data || '').slice(1);

export function getBase64(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
  });
}

export function getBinary(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const data = (reader?.result as any)?.split(',')[1];
      resolve(atob(data));
    };
    reader.onerror = (err) => reject(err);
  });
}

export const checkOddValue = (data: string) =>
  ['undefined', 'null'].includes(data);

export const statusType = {
  success: { col: '!text-[#12B76A]', bg: '!bg-[#ECFDF3]' },
  pending: { col: '!text-[#FFBD00]', bg: '!bg-[#FFBD001F]' },
  error: { col: '!text-[#E01020]', bg: '!bg-[#E010201F]' },
  info: { col: '!text-[#495057]', bg: '!bg-[#878A991F]' },
};

export const handleTableSelect = (data: any, setData: any, key: string) => ({
  type: 'checkbox',
  selectedRowKeys: data,
  onChange: (e: any, selectedRows: any) => {
    const arr: any[] = [];
    selectedRows.forEach((row: any) => arr.push(row?.[key] || row));
    setData(arr);
  },
});

export function handleAPIMerge(url: string, path: string, other?: boolean) {
  if (other) return path;
  const newPath = path?.slice(1);
  const newUrl = url?.slice(0, -1);
  const isUrlSl = url?.slice(-1) === '/';
  const mainUrl = isUrlSl ? newUrl : url;
  const isPathSl = path?.slice(0, 1) === '/';
  const mainPath = isPathSl ? newPath : path;
  return mainUrl?.concat('/', mainPath);
}

export function handleBase64(data: string) {
  const split = data?.split(',');
  const prefix = 'data:audio/wav;base64';
  const isCorrect = isEqual(split?.[0], prefix);
  if (!isCorrect) return [prefix, data]?.join(',');
  return data;
}

export const handleArray = (data: any[], other?: any) =>
  Array.isArray(data || []) && (data || [])?.length ? data : other || [];

export const validatePassword = (pass: string) => {
  const criteria = [
    { regex: /.{8,}/, description: 'At least 8 characters' },
    { regex: /[A-Z]/, description: 'At least one uppercase letter' },
    { regex: /[0-9]/, description: 'At least one number' },
    { regex: /[!@#$^&*()]/, description: 'At least one special character' },
  ];

  return criteria.map((criterion) => ({
    ...criterion,
    isValid: criterion.regex.test(pass),
  }));
};

export const getSubdomain = () => {
  if (typeof window === 'undefined') return null;

  const host = window.location.hostname;
  const parts = host.split('.');

  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }

  if (parts.length === 2 && parts[1] === 'localhost') {
    return parts[0];
  }

  if (parts.length >= 3) {
    return parts[0];
  }

  return null;
};

export const getFirstTwoLetters = (text: string) => {
  const array = text.split(" ")
  if (array.length >= 2) {
    const firstLetter = array[0][0]
    const secondLetter = array[1][0]

    return `${firstLetter}${secondLetter}`
  }

  return array[0][0]
}

export const useHandleAppStateChange = () => {
  const setAppState = useSetRecoilState(appAtom);

  const changeCompany = (company: { value: string, label: string } | null) => {
    setAppState((prev) => ({
      ...prev,
      company_id: company ?? null,
    }));
  };

  const changeEstate = (estate: { value: string, label: string } | null) => {
    setAppState((prev) => ({
      ...prev,
      estate_id: estate ?? null,
    }));
  };
  return { changeCompany, changeEstate };
};

export const useCheckUserRole = () => {
  const { user } = useRecoilValue(authAtom);
  const userRole = user?.role?.slug;

  const isSuperAdminLevel = () => {
    const superLevelAdmins = ["super-admin"]
    return superLevelAdmins.includes(userRole)
  }

  const isCompanyLevel = () => {
    const companyLevelAdmins = ["company-manager"]
    return companyLevelAdmins.includes(userRole)

  }
  const isEstateLevel = () => {
    const estateLevelAdmin = ["estate-manager"]
    return estateLevelAdmin.includes(userRole)

  }

  return { isSuperAdminLevel, isCompanyLevel, isEstateLevel }
}


// export const handleConfirm = ({
//   title,
//   description = "Are you sure you want to carry out this action?",
//   onConfirm,
//   okText = 'Yes',
//   cancelText = 'Cancel',
//   loading
// }: {
//   title: string;
//   description: string,
//   onConfirm: () => Promise<any> | void;
//   okText?: string;
//   cancelText?: string
//   loading?: boolean
// }) => {
//   Modal.confirm({
//     okButtonProps: { className: "!border-none !bg-primary text-white hover:!bg-primary/90 hover:!text-white", loading: loading },
//     title,
//     content: description,
//     okText,
//     cancelText,
//     centered: true,

//     okType: 'danger',
//     onOk: async () => {
//       try {
//         await onConfirm?.();
//       } catch (e) {
//         return Promise.reject(e);
//       }
//     }
//   });
// };